import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Table, Tag, Progress, Button, message, Space, Popconfirm, Tooltip, Input, Card, Flex, Grid } from 'antd';
import { DeleteOutlined, DownloadOutlined, SearchOutlined, ExportOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getDataExports, deleteDataExport } from './DataExportImportServices';
import { Resource, Action } from '../../Constants/socket';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import DataExportForm from './DataExportForm';

const { useBreakpoint } = Grid;

const DataExportList = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { user: { id: currentUserId } = {} } = useGlobal();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Subscribe to DATA_EXPORT resource
    const exportIds = useMemo(() => data.map(item => item.id), [data]);
    useSocketSubscription(Resource.DATA_EXPORT, exportIds);

    // Listen for updates
    const handleExportUpdate = useCallback((updatedExport) => {
        setData(prevData => prevData.map(item => item.id === updatedExport.id ? updatedExport : item));
    }, []);

    const handleExportCreate = useCallback(() => {
        fetchData(pagination.current, searchText);
    }, [pagination.current, searchText]);

    useSocketEvent(Resource.DATA_EXPORT, Action.UPDATE, handleExportUpdate);
    useSocketEvent(Resource.DATA_EXPORT, Action.CREATE, handleExportCreate);

    const fetchData = useCallback(async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await getDataExports({
                page,
                page_size: pagination.pageSize,
                sort_by: 'created_at',
                sort_direction: 'desc',
            });
            setData(response.data);
            setPagination(prev => ({
                ...prev,
                current: response.metadata.page,
                total: response.metadata.total
            }));
        } catch (error) {
            message.error(t('failed_to_fetch_exports'));
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize, t]);

    const debouncedFetch = useMemo(
        () => debounce((page, search) => {
            fetchData(page, search);
        }, 500),
        [fetchData]
    );

    useEffect(() => {
        fetchData(pagination.current, searchText);
    }, [fetchData]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedFetch(1, value);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const response = await deleteDataExport(id);
            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);
            fetchData(pagination.current, searchText);
        } catch (error) {
            message.error(t('failed_to_delete_export'));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString(),
            width: 180,
        },
        {
            title: t('resources'),
            dataIndex: 'resources',
            key: 'resources',
            render: (resources) => resources?.map(r => <Tag key={r}>{r}</Tag>),
        },
        {
            title: t('format'),
            dataIndex: 'format',
            key: 'format',
            render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>,
            width: 100,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            width: 200,
            render: (statusObj, record) => {
                let status = 'unknown';
                if (statusObj && typeof statusObj === 'object') {
                    if (statusObj.message_code) {
                        const parts = statusObj.message_code.split('.');
                        status = parts[parts.length - 1];
                    } else if (statusObj.message) {
                        status = statusObj.message;
                    }
                } else if (typeof statusObj === 'string') {
                    status = statusObj;
                }

                let color = 'default';
                if (status === 'completed') color = 'success';
                else if (status === 'failed') color = 'error';
                else if (status === 'processing') color = 'processing';

                return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Tag color={color}>
                            {statusObj && typeof statusObj === 'object'
                                ? resolveBackendMessage(t, statusObj)
                                : status}
                        </Tag>
                        {status === 'processing' && (
                            <Progress percent={Math.round(record.progress || 0)} size="small" />
                        )}
                    </Space>
                );
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => {
                const statusObj = record.status;
                let isCompleted = false;
                if (statusObj && typeof statusObj === 'object' && statusObj.message_code) {
                    isCompleted = statusObj.message_code.endsWith('completed');
                } else {
                    isCompleted = statusObj === 'completed';
                }

                return (
                    <Space>
                        {isCompleted && record.download_url && (
                            <Tooltip title={t('download')}>
                                <Button
                                    type="text"
                                    icon={<DownloadOutlined />}
                                    href={record.download_url}
                                    target="_blank"
                                />
                            </Tooltip>
                        )}
                        <Popconfirm
                            title={t('delete_export_confirm')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        }
    ];

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize }));
        fetchData(page, searchText);
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <DataExportForm open={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <Card
                title={t('exports')}
                extra={
                    <Button
                        icon={<ExportOutlined />}
                        onClick={() => setIsModalOpen(true)}
                        type="primary"
                    >
                        {!screens.xs && t('create_export')}
                    </Button>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="space-between"
                    style={{ width: '100%' }}
                >
                    <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_exports')}
                            allowClear
                            value={searchText}
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>
                </Flex>
            </Card>
            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </Space>
    );
};

export default DataExportList;
